/**
 * Exhaustive enum of all permission tree_keys used throughout the application.
 *
 * Every call to hasPermission() uses one of these values, so typos are caught
 * at compile time rather than silently evaluating to false at runtime.
 */
export enum Permission {
  // -- special sentinel --
  PermissionNotDefined = "permission-not-defined",

  // -- dashboard --
  Dashboard = "dashboard",

  // -- monitoring --
  MonitorControlPanelCarCalls = "monitor-control-panel-car-calls",
  MonitorControlPanelHallCalls = "monitor-control-panel-hall-calls",
  MonitorControlPanelActions = "monitor-control-panel-actions",
  MonitorControlPanelFaults = "monitor-control-panel-faults",
  MonitorControlPanelStatus = "monitor-control-panel-status",
  /**
   * Gates the lift control panel's State tab. That tab used to borrow
   * `monitor-control-panel-faults`, so a role could not have Faults without State
   * (or the reverse); it has its own key now so the Roles editor's Monitoring
   * Dashboard State switch can grant it alone.
   */
  MonitorControlPanelState = "monitor-control-panel-state",
  /**
   * A UI pseudo-key: LiftCloud's seed_menu.json carries it, this catalogue did
   * not, so the roles editor could not offer a grant the server already knew
   * about. Added to close that half of the divergence rather than to introduce
   * a new capability -- nothing in this app gates on it yet.
   */
  MonitorControlPanelVipMode = "monitor-control-panel-vip-mode",
  MonitorControlPanelSecureHallCalls = "monitor-control-panel-secure-hall-calls",
  MonitorControlPanelSecureCarCalls = "monitor-control-panel-secure-car-calls",
  /** Opens the BANK control panel from the monitoring card's bank name. */
  MonitorControlPanelBanks = "monitor-control-panel-banks",
  /** Opens the LIFT control panel from a lift's name in any monitoring view. */
  MonitorControlPanelLifts = "monitor-control-panel-lifts",
  /**
   * Gates the bank-configuration section of the BANK control panel. A UI
   * pseudo-key: LiftCloud's seed_menu.json and its control-panel role-menu
   * backfill (role_menu_control_panel_backfill.go, uri = "/" + tree_key) both
   * carry it; this catalogue did not, so the roles editor could not offer a
   * grant the server already knew about.
   */
  MonitorControlPanelBankConfiguration = "monitor-control-panel-bank-configuration",
  MonitorEmulator = "monitor-emulator",
  MonitorGridTypeMapView = "monitor-grid-type-map-view",

  // -- scheduled actions --
  ScheduledActionsView = "scheduled-actions-view",
  ScheduledActionsCreate = "scheduled-actions-create",
  ScheduledActionsUpdate = "scheduled-actions-update",
  ScheduledActionsDelete = "scheduled-actions-delete",

  // -- edge management --
  ViewEdgeManagement = "view-edge-management",
  ViewEdgeManagementAdapters = "view-edge-management-adapters",
  ViewEdgeManagementBanks = "view-edge-management-banks",
  ViewEdgeManagementCatalog = "view-edge-management-catalog",
  ViewEdgeManagementChannels = "view-edge-management-channels",
  ViewEdgeManagementGroups = "view-edge-management-groups",
  ViewEdgeManagementLifts = "view-edge-management-lifts",
  ViewEdgeManagementMap = "view-edge-management-map",
  ViewEdgeManagementServers = "view-edge-management-servers",

  // -- edge management CRUD --
  AdaptersCreate = "adapters-create",
  AdaptersCsvCreate = "adapters-csv-create",
  AdaptersDelete = "adapters-delete",
  AdaptersUpdate = "adapters-update",
  AdaptersView = "adapters-view",
  BanksCreate = "banks-create",
  BanksDelete = "banks-delete",
  BanksUpdate = "banks-update",
  BanksView = "banks-view",
  ChannelsDelete = "channels-delete",
  ChannelsEdit = "channels-edit",
  ChannelsView = "channels-view",
  ConfigurationsCreate = "configurations-create",
  ConfigurationsDelete = "configurations-delete",
  ConfigurationsUpdate = "configurations-update",
  ConfigurationsView = "configurations-view",
  GroupsDelete = "groups-delete",
  GroupsUpdate = "groups-update",
  GroupsView = "groups-view",
  LiftsDelete = "lifts-delete",
  LiftsUpdate = "lifts-update",
  LiftsView = "lifts-view",
  MapsDelete = "maps-delete",
  MapsUpdate = "maps-update",
  ServersDelete = "servers-delete",
  ServersExport = "servers-export",
  ServersImport = "servers-import",
  ServersOauthCreate = "servers-oauth-create",
  ServersUpdate = "servers-update",
  ServersView = "servers-view",

  // -- monitoring dashboard --
  /**
   * Shows the sidebar's Monitoring Dashboard link and lets the /monitoring
   * screen render. Deliberately ONLY a screen gate: the calls the screen makes
   * are authorised server-side by their own keys, so granting this alone shows
   * an empty page. The Roles editor's Monitoring Dashboard switch enables the
   * whole set at once (see permissionBundles.ts).
   */
  ViewMonitoringDashboard = "view-monitoring-dashboard",

  // -- organization management --
  ViewOrganizationManagement = "view-organization-management",
  ViewOrganizationManagementOrganizations = "view-organization-management-organizations",
  ViewOrganizationManagementRoles = "view-organization-management-roles",
  ViewOrganizationManagementUsers = "view-organization-management-users",
  Organizations = "organizations",
  OrgsDelete = "orgs-delete",
  OrgsUpdate = "orgs-update",
  /**
   * Opens an organization in the setup wizard from Organization Management
   * (the Organizations table's "Edit in wizard", "Edit my organization"), and
   * reads the Organizations list those sit on. Held by every role that holds
   * OrgsUpdate, the gate those entry points used before this key existed. The
   * wizard's per-page operations are in pages/setupWizard/wizardPermissions.ts.
   */
  WizardInstallerAccess = "wizard-installer-access",
  WizardInstallerDraftsView = "wizard-installer-drafts-view",
  WizardInstallerDraftsUpdate = "wizard-installer-drafts-update",
  WizardInstallerDraftsDelete = "wizard-installer-drafts-delete",

  // -- wizard-installer: one per operation per setup-wizard page --
  // Operation keys (wizard-<page>-<op>) are what the wizard gates on, through
  // pages/setupWizard/wizardPermissions.ts. The rest (wizard-<page>-<route
  // key>) are LiftCloud's route mirrors each operation holds; listed so this
  // enum stays exhaustive against the catalogue.
  WizardInstallerOrgsView = "wizard-installer-orgs-view",
  WizardInstallerOrgsViewLight = "wizard-installer-orgs-view-light",
  WizardOrganizationView = "wizard-organization-view",
  WizardOrganizationCreate = "wizard-organization-create",
  WizardOrganizationUpdate = "wizard-organization-update",
  WizardOrganizationDelete = "wizard-organization-delete",
  WizardOrganizationOrgsView = "wizard-organization-orgs-view",
  WizardOrganizationHolidaysView = "wizard-organization-holidays-view",
  WizardOrganizationOrgsCreate = "wizard-organization-orgs-create",
  WizardOrganizationHolidaysCreate = "wizard-organization-holidays-create",
  WizardOrganizationOrgsUpdate = "wizard-organization-orgs-update",
  WizardOrganizationOrgsSettingsUpdate = "wizard-organization-orgs-settings-update",
  WizardOrganizationHolidaysUpdate = "wizard-organization-holidays-update",
  WizardOrganizationOrgsDelete = "wizard-organization-orgs-delete",
  WizardOrganizationHolidaysDelete = "wizard-organization-holidays-delete",
  WizardServerView = "wizard-server-view",
  WizardServerCreate = "wizard-server-create",
  WizardServerUpdate = "wizard-server-update",
  WizardServerDelete = "wizard-server-delete",
  WizardServerServersView = "wizard-server-servers-view",
  WizardServerServersCreate = "wizard-server-servers-create",
  WizardServerServersUpdate = "wizard-server-servers-update",
  WizardServerServersDelete = "wizard-server-servers-delete",
  WizardAdaptersView = "wizard-adapters-view",
  WizardAdaptersCreate = "wizard-adapters-create",
  WizardAdaptersUpdate = "wizard-adapters-update",
  WizardAdaptersDelete = "wizard-adapters-delete",
  WizardAdaptersAdaptersView = "wizard-adapters-adapters-view",
  WizardAdaptersKeysView = "wizard-adapters-keys-view",
  WizardAdaptersAdaptersCreate = "wizard-adapters-adapters-create",
  WizardAdaptersAdaptersUpdate = "wizard-adapters-adapters-update",
  WizardAdaptersAdaptersDelete = "wizard-adapters-adapters-delete",
  WizardChannelsView = "wizard-channels-view",
  WizardChannelsCreate = "wizard-channels-create",
  WizardChannelsUpdate = "wizard-channels-update",
  WizardChannelsDelete = "wizard-channels-delete",
  WizardChannelsChannelsView = "wizard-channels-channels-view",
  WizardChannelsChannelEndpointsView = "wizard-channels-channel-endpoints-view",
  WizardChannelsControlRoutesView = "wizard-channels-control-routes-view",
  WizardChannelsBanksByServerView = "wizard-channels-banks-by-server-view",
  WizardChannelsChannelsCreate = "wizard-channels-channels-create",
  WizardChannelsControlRoutesCreate = "wizard-channels-control-routes-create",
  WizardChannelsChannelsEdit = "wizard-channels-channels-edit",
  WizardChannelsControlRoutesEdit = "wizard-channels-control-routes-edit",
  WizardChannelsChannelsDelete = "wizard-channels-channels-delete",
  WizardChannelsControlRoutesDelete = "wizard-channels-control-routes-delete",
  WizardBanksView = "wizard-banks-view",
  WizardBanksCreate = "wizard-banks-create",
  WizardBanksUpdate = "wizard-banks-update",
  WizardBanksDelete = "wizard-banks-delete",
  WizardBanksBanksView = "wizard-banks-banks-view",
  WizardBanksBanksByServerView = "wizard-banks-banks-by-server-view",
  WizardBanksLiftsView = "wizard-banks-lifts-view",
  WizardBanksBanksCreate = "wizard-banks-banks-create",
  WizardBanksLiftsCreate = "wizard-banks-lifts-create",
  WizardBanksBanksUpdate = "wizard-banks-banks-update",
  WizardBanksBanksPinAssignmentsUpdate = "wizard-banks-banks-pin-assignments-update",
  WizardBanksBanksDelete = "wizard-banks-banks-delete",
  WizardBanksBanksFloorsDelete = "wizard-banks-banks-floors-delete",
  WizardBanksLiftsDelete = "wizard-banks-lifts-delete",
  WizardBanksBanksDeletedView = "wizard-banks-banks-deleted-view",
  WizardBanksBanksRestore = "wizard-banks-banks-restore",
  WizardChannelModifiersView = "wizard-channel-modifiers-view",
  WizardChannelModifiersCreate = "wizard-channel-modifiers-create",
  WizardChannelModifiersUpdate = "wizard-channel-modifiers-update",
  WizardChannelModifiersDelete = "wizard-channel-modifiers-delete",
  WizardChannelModifiersChannelModifiersView = "wizard-channel-modifiers-channel-modifiers-view",
  WizardChannelModifiersChannelsModifiersView = "wizard-channel-modifiers-channels-modifiers-view",
  WizardChannelModifiersChannelModifiersCreate = "wizard-channel-modifiers-channel-modifiers-create",
  WizardChannelModifiersChannelModifiersEdit = "wizard-channel-modifiers-channel-modifiers-edit",
  WizardChannelModifiersChannelModifiersDelete = "wizard-channel-modifiers-channel-modifiers-delete",
  WizardLiftsView = "wizard-lifts-view",
  WizardLiftsCreate = "wizard-lifts-create",
  WizardLiftsUpdate = "wizard-lifts-update",
  WizardLiftsDelete = "wizard-lifts-delete",
  WizardLiftsLiftsView = "wizard-lifts-lifts-view",
  WizardLiftsBanksView = "wizard-lifts-banks-view",
  WizardLiftsLiftsCreate = "wizard-lifts-lifts-create",
  WizardLiftsLiftsUpdate = "wizard-lifts-lifts-update",
  WizardLiftsBanksUpdate = "wizard-lifts-banks-update",
  WizardLiftsLiftsDelete = "wizard-lifts-lifts-delete",
  WizardDebounceView = "wizard-debounce-view",
  WizardDebounceCreate = "wizard-debounce-create",
  WizardDebounceUpdate = "wizard-debounce-update",
  WizardDebounceDelete = "wizard-debounce-delete",
  WizardDebounceDebounceOverridesView = "wizard-debounce-debounce-overrides-view",
  WizardDebounceBanksView = "wizard-debounce-banks-view",
  WizardDebounceDebounceOverridesCreate = "wizard-debounce-debounce-overrides-create",
  WizardDebounceDebounceOverridesUpdate = "wizard-debounce-debounce-overrides-update",
  WizardDebounceDebounceOverridesDelete = "wizard-debounce-debounce-overrides-delete",
  WizardBacnetView = "wizard-bacnet-view",
  WizardBacnetUpdate = "wizard-bacnet-update",
  WizardBacnetAdaptersView = "wizard-bacnet-adapters-view",
  WizardBacnetKeysView = "wizard-bacnet-keys-view",
  WizardBacnetAdaptersUpdate = "wizard-bacnet-adapters-update",
  WizardUsersRolesView = "wizard-users-roles-view",
  WizardUsersRolesCreate = "wizard-users-roles-create",
  WizardUsersRolesUpdate = "wizard-users-roles-update",
  WizardUsersRolesDelete = "wizard-users-roles-delete",
  WizardUsersRolesUsersView = "wizard-users-roles-users-view",
  WizardUsersRolesRolesView = "wizard-users-roles-roles-view",
  WizardUsersRolesRolesViewLight = "wizard-users-roles-roles-view-light",
  WizardUsersRolesUsersCreate = "wizard-users-roles-users-create",
  WizardUsersRolesRolesCreate = "wizard-users-roles-roles-create",
  WizardUsersRolesUsersUpdate = "wizard-users-roles-users-update",
  WizardUsersRolesRolesUpdate = "wizard-users-roles-roles-update",
  WizardUsersRolesUsersDelete = "wizard-users-roles-users-delete",
  WizardUsersRolesRolesDelete = "wizard-users-roles-roles-delete",
  WizardNotificationsView = "wizard-notifications-view",
  WizardNotificationsCreate = "wizard-notifications-create",
  WizardNotificationsUpdate = "wizard-notifications-update",
  WizardNotificationsDelete = "wizard-notifications-delete",
  WizardNotificationsNotificationsView = "wizard-notifications-notifications-view",
  WizardNotificationsNotificationsAssignView = "wizard-notifications-notifications-assign-view",
  WizardNotificationsNotificationsCreate = "wizard-notifications-notifications-create",
  WizardNotificationsNotificationsAssignCreate = "wizard-notifications-notifications-assign-create",
  WizardNotificationsNotificationsUpdate = "wizard-notifications-notifications-update",
  WizardNotificationsNotificationsAssignUpdate = "wizard-notifications-notifications-assign-update",
  WizardNotificationsNotificationsDelete = "wizard-notifications-notifications-delete",
  WizardNotificationsNotificationsAssignDelete = "wizard-notifications-notifications-assign-delete",
  WizardActionsView = "wizard-actions-view",
  WizardActionsCreate = "wizard-actions-create",
  WizardActionsUpdate = "wizard-actions-update",
  WizardActionsDelete = "wizard-actions-delete",
  WizardActionsActionsViewByOrganization = "wizard-actions-actions-view-by-organization",
  WizardActionsActionBanksView = "wizard-actions-action-banks-view",
  WizardActionsActionsCreate = "wizard-actions-actions-create",
  WizardActionsActionsUpdate = "wizard-actions-actions-update",
  WizardActionsActionBanksUpdate = "wizard-actions-action-banks-update",
  WizardActionsActionsDelete = "wizard-actions-actions-delete",
  WizardMonitorGridView = "wizard-monitor-grid-view",
  WizardMonitorGridCreate = "wizard-monitor-grid-create",
  WizardMonitorGridUpdate = "wizard-monitor-grid-update",
  WizardMonitorGridDelete = "wizard-monitor-grid-delete",
  WizardMonitorGridMonitorGridByOrganization = "wizard-monitor-grid-monitor-grid-by-organization",
  WizardMonitorGridMonitorGridView = "wizard-monitor-grid-monitor-grid-view",
  WizardMonitorGridMonitorGridCreate = "wizard-monitor-grid-monitor-grid-create",
  WizardMonitorGridMonitorGridUpdate = "wizard-monitor-grid-monitor-grid-update",
  WizardMonitorGridMonitorGridDelete = "wizard-monitor-grid-monitor-grid-delete",
  WizardReviewView = "wizard-review-view",
  WizardReviewUpdate = "wizard-review-update",
  WizardReviewOrgsSettingsUpdate = "wizard-review-orgs-settings-update",
  OrgsView = "orgs-view",
  RolesCreate = "roles-create",
  RolesDelete = "roles-delete",
  RolesUpdate = "roles-update",
  RolesView = "roles-view",

  // -- users --
  Users = "users",
  UsersBlockUpdate = "users-block-update",
  UsersCreate = "users-create",
  UsersDelete = "users-delete",
  UsersOfflinePswUserCreate = "users-offline-psw-user-create",
  UsersOfflinePswUserView = "users-offline-psw-user-view",
  /**
   * Change ANOTHER user's action firing mode.
   *
   * Distinct from UsersActionFiringModeUpdate, which is your own. The server
   * can only tell them apart because they are different URIs -- Authorize
   * derives the grant from the request path -- and the two differ by exactly
   * the trailing slash that sanitizeURI leaves once it strips the user id.
   */
  UsersActionFiringModeUserUpdate = "users-action-firing-mode-user-update",
  UsersResetUpdate = "users-reset-update",
  UsersUnblockUpdate = "users-unblock-update",
  UsersUpdate = "users-update",
  UsersView = "users-view",

  // -- reports management --
  ViewReportManagement = "view-report-management",
  FaultReport = "fault-report",
  BeneficialReport = "beneficial-report",
  CarReport = "car-report",
  OutOfServiceReport = "out-of-service-report",
  PerformanceReport = "performance-report",
  ServiceReport = "service-report",
  TrafficReport = "traffic-report",
  ViewReportManagementCarServiceLog = "view-report-management-car-service-log",
  ViewReportManagementCarServiceServiceLogChart = "view-report-management-car-service-service-log-chart",
  ViewReportManagementCarServiceServiceLogTable = "view-report-management-car-service-service-log-table",
  ViewReportManagementCarServiceStatistics = "view-report-management-car-service-statistics",
  ViewReportManagementCarUseByHour = "view-report-management-car-use-by-hour",
  ViewReportManagementCarUseComparisons = "view-report-management-car-use-comparisons",
  ViewReportManagementCarUseDoorTimes = "view-report-management-car-use-door-times",
  ViewReportManagementFaultsBycar = "view-report-management-faults-by-car",
  ViewReportManagementFaultsByCarEscmov = "view-report-management-faults-by-car-escmov",
  ViewReportManagementFaultsByDayWeek = "view-report-management-faults-by-day-week",
  ViewReportManagementFaultsByDayWeekEscmov = "view-report-management-faults-by-day-week-escmov",
  ViewReportManagementFaultsByFloor = "view-report-management-faults-by-floor",
  ViewReportManagementFaultsByType = "view-report-management-faults-by-type",
  ViewReportManagementFaultsByTypeEscmov = "view-report-management-faults-by-type-escmov",
  ViewReportManagementFaultsLogByBank = "view-report-management-faults-log-by-bank",
  ViewReportManagementFaultsRecentFaults = "view-report-management-faults-recent-faults",
  ViewReportManagementFaultsRecentFaultsEscmov = "view-report-management-faults-recent-faults-escmov",
  ViewReportManagementFaultsFrequencyByCar = "view-report-management-faults-frequency-by-car",
  ViewReportManagementFaultsFrequencyByFault = "view-report-management-faults-frequency-by-fault",
  ViewReportManagementGeneralFireService = "view-report-management-general-fire-service",
  ViewReportManagementGeneralBeneficialUsage = "view-report-management-general-beneficial-usage",
  ViewReportManagementGeneralCurrentOutOfService = "view-report-management-general-current-out-of-service",
  ViewReportManagementGeneralEscalatorInUse = "view-report-management-general-escalator-in-use",
  ViewReportManagementGeneralPerformance = "view-report-management-general-performance",
  ViewReportManagementTrafficAverageByFloor = "view-report-management-traffic-average-by-floor",
  ViewReportManagementTrafficAverageByTimeOfDay = "view-report-management-traffic-average-by-time-of-day",
  ViewReportManagementTrafficDetailedSummaryReport = "view-report-management-traffic-detailed-summary-report",
  ViewReportManagementTrafficDistributionByHourlyInterval = "view-report-management-traffic-distribution-by-hourly-interval",
  ViewReportManagementTrafficDistributionDetaileDirection = "view-report-management-traffic-distribution-detaile-direction",
  ViewReportManagementTrafficDistributionDetailedTotal = "view-report-management-traffic-distribution-detailed-total",
  ViewReportManagementTrafficHallButtonCountByTimeOfDay = "view-report-management-traffic-hall-button-count-by-time-of-day",
  ViewReportManagementTrafficHallButtonCountsByFloor = "view-report-management-traffic-hall-button-counts-by-floor",
  ViewReportManagementTrafficHallCallSummaryReport = "view-report-management-traffic-hall-call-summary-report",
  ViewReportManagementTrafficLongestWaitTimesList = "view-report-management-traffic-longest-wait-times-list",
  ViewReportManagementTrafficTripsToAndFromFloor = "view-report-management-traffic-trips-to-and-from-floor",

  // -- tools management --
  ViewToolManagementActions = "view-tool-management-actions",
  ViewToolManagementDataBlocks = "view-tool-management-data-blocks",
  ViewToolManagementMessages = "view-tool-management-messages",
  ViewToolManagementMessagesType = "view-tool-management-messages-type",
  ViewToolManagementOffsetTypes = "view-tool-management-offset-types",
  ViewToolManagementPermissions = "view-tool-management-permissions",
  ViewToolManagementVendors = "view-tool-management-vendors",
  DataBlocksCatalog = "data-blocks-catalog",
  DatablocksCreate = "datablocks-create",
  Definitions = "definitions",
  Offsets = "offsets",
  ProtocolDelete = "protocol-delete",
  ProtocolUpdate = "protocol-update",
  ProtocolView = "protocol-view",
  VendorsCatalog = "vendors-catalog",
  LeadingZeroesDelete = "leading-zeroes-delete",
  LeadingZeroesUpdate = "leading-zeroes-update",
  LeadingZeroesView = "leading-zeroes-view",
  OffsetTypesCatalog = "offset-types-catalog",
  KeysImportCreate = "keys-import-create",
  CustomKeysImportCreate = "custom-keys-import-create",

  // -- misc --
  HolidaysView = "holidays-view",
  NotificationsView = "notifications-view",

  // -------------------------------------------------------------------------
  // Backend catalogue — every remaining action tree_key in LiftCloud's permission catalogue.
  // The enum is exhaustive against the catalogue: roleMenu.test.ts asserts that
  // every action key of the test fixture copy (src/test-utils) has a constant
  // here, so a key added without one fails the build rather than surfacing
  // later as a raw string at a call site.
  // -------------------------------------------------------------------------

  // -- view-monitoring-dashboard --
  ViewMonitoringDashboardAction = "view-monitoring-dashboard-action",

  // -- view-organization-management-organizations --
  ViewOrganizationManagementOrganizationsAction = "view-organization-management-organizations-action",

  // -- view-organization-management-roles --
  ViewOrganizationManagementRolesAction = "view-organization-management-roles-action",

  // -- view-organization-management-users --
  ViewOrganizationManagementUsersAction = "view-organization-management-users-action",

  // -- monitoring --
  MonitorGridWsView = "monitor-grid-ws-view",
  LiftRecordsDashboardWsView = "lift-records-dashboard-ws-view",
  MonitorGridByUser = "monitor-grid-by-user",
  MonitorGridByOrganization = "monitor-grid-by-organization",
  MonitorGridView = "monitor-grid-view",
  MonitorGridCreate = "monitor-grid-create",
  MonitorGridDelete = "monitor-grid-delete",
  MonitorGridCommandCreate = "monitor-grid-command-create",
  MonitorGridCommandAdapterCreate = "monitor-grid-command-adapter-create",
  MonitorGridUpdate = "monitor-grid-update",
  MonitorView = "monitor-view",

  // -- organizations --
  OrgsParentsView = "orgs-parents-view",
  OrgsChildrenView = "orgs-children-view",
  OrgsCreate = "orgs-create",
  OrgsViewLight = "orgs-view-light",
  OrgsSettingsUpdate = "orgs-settings-update",

  // -- roles --
  RolesViewLight = "roles-view-light",
  /**
   * Reads a role's permission history (GET /api/roles/:roleID/permission-history,
   * LIFT-1092). LiftCloud gives it by rule to every role holding roles-view, so
   * a role that can see roles sees their history unless it was switched off.
   */
  RolesPermissionHistoryView = "roles-permission-history-view",

  // -- holidays --
  HolidaysCreate = "holidays-create",
  HolidaysUpdate = "holidays-update",
  HolidaysDelete = "holidays-delete",

  // -- users --
  UsersMeView = "users-me-view",
  UsersTermsCreate = "users-terms-create",
  UsersOfflinePswCreate = "users-offline-psw-create",
  UsersOfflinePswView = "users-offline-psw-view",
  /** Change your OWN action firing mode. See UsersActionFiringModeUserUpdate. */
  UsersActionFiringModeUpdate = "users-action-firing-mode-update",
  UsersResendUpdate = "users-resend-update",
  UsersSetUpdate = "users-set-update",

  // -- github_token --
  /** Gates `PUT /api/github-token/` — writing the org's GitHub PAT. */
  GithubTokenUpdate = "github-token-update",
  /** Gates `DELETE /api/github-token/` — clearing the org's GitHub PAT. */
  GithubTokenDelete = "github-token-delete",

  // -- servers --
  ServersCreate = "servers-create",
  ServersViewLight = "servers-view-light",
  /** Reads an edge server's provisioning config by MAC (GET /api/edge/config/:mac). */
  ServersEdgeProvisionConfigView = "servers-edge-provision-config-view",

  // -- banks --
  BanksViewLight = "banks-view-light",
  BanksViewOrg = "banks-view-org",
  BanksByServerView = "banks-by-server-view",
  BanksCompleteCreate = "banks-complete-create",
  BanksActionsUpdate = "banks-actions-update",
  /**
   * Gates `GET /api/action-banks/by-bank/:bankID` server-side, verb "view" —
   * the one endpoint every action button in monitoring comes from.
   *
   * LiftCloud's `utils.Authorize` matches this by (name, uri) against the
   * caller's STORED menu and grants master admin no bypass, unlike
   * `useMenuPermission` here. See monitoring-control-panel.md for why a role
   * can look fully granted and still get 401 on it.
   */
  ActionBanksByBankView = "action-banks-by-bank-view",
  BanksMessagesDelete = "banks-messages-delete",
  BanksFloorsDelete = "banks-floors-delete",
  BanksNotifications = "banks-notifications",
  BanksNotificationsAssignmentsView = "banks-notifications-assignments-view",
  BanksSummaryView = "banks-summary-view",
  /**
   * Gates `PUT /api/banks/:bankID/pin-assignments` server-side, verb "update".
   *
   * `sanitizeURI` strips the UUID and collapses the resulting `//`, so the pair
   * LiftCloud looks for is exactly ("update", "/api/banks/pin-assignments") —
   * no trailing slash. The wizard's Banks step writes here on every save
   * (StepBanks.tsx), so a role without this node cannot complete that step.
   *
   * The same tree_key already exists in LiftCloud's `initializers/seed_menu.json`;
   * this restores the frontend catalogue to parity with it rather than inventing
   * a new node.
   */
  BanksPinAssignmentsUpdate = "banks-pin-assignments-update",
  /**
   * Gates `GET /api/banks/deleted/` server-side, verb "view" — the list of
   * soft-deleted banks behind the wizard's "Deleted banks" panel and the Banks
   * page's deleted section.
   *
   * The catalogue URI carries a trailing slash, unlike `banks-restore`, and
   * LiftCloud matches (name, uri) exactly, so the two catalogues must agree byte
   * for byte or the grant reaches nothing.
   */
  BanksDeletedView = "banks-deleted-view",
  /**
   * Gates `POST /api/banks/:bankID/restore` server-side, verb "create". The UUID
   * param collapses and the route resolves to ("create", "/api/banks/restore"),
   * no trailing slash. Seeded for every role that already holds `banks-delete`.
   */
  BanksRestore = "banks-restore",
  /**
   * Gates `PUT /api/action-banks/:messageID/:bankID` server-side, verb "update".
   *
   * Both path params are UUIDs (`formatUuidT` at StepActions.tsx:2849-2850), so
   * `sanitizeURI` reduces the path to "/api/action-banks/". `GetURIAction`
   * matches (name, uri) EXACTLY, so this grants the PUT alone: POST resolves to
   * verb "create" and DELETE to "delete", neither of which is catalogued.
   *
   * Sole caller is the setup wizard's Actions step, which writes an operator's
   * visual button configuration back to the action_bank row. LiftCloud's
   * `utils.Authorize` gives master admin no bypass on this menu check, so
   * without this node that save fails for every role.
   */
  ActionBanksUpdate = "action-banks-update",
  /**
   * Gates BOTH `GET /api/action-banks/` and `GET
   * /api/action-banks/:messageID/:bankID`: the UUID params collapse, so both
   * routes resolve to the same ("/api/action-banks/", "view") pair
   * (LiftCloud ISC-127, ISC-128).
   */
  ActionBanksView = "action-banks-view",
  /** Gates `GET /api/action-banks/by-organization/:organizationID` (ISC-129). */
  ActionBanksByOrganizationView = "action-banks-by-organization-view",
  /** Gates `POST /api/action-banks/` (ISC-130). */
  ActionBanksCreate = "action-banks-create",
  /** Gates `DELETE /api/action-banks/:messageID/:bankID` (ISC-131). */
  ActionBanksDelete = "action-banks-delete",

  // -- channels --
  ChannelsCreate = "channels-create",

  // -- lifts --
  LiftsCreate = "lifts-create",
  LiftsLightView = "lifts-light-view",
  LiftsByBankView = "lifts-by-bank-view",

  // -- groups --
  GroupsCreate = "groups-create",

  // -- adapters --
  AdaptersUpdateSummary = "adapters-update-summary",
  AdaptersUpdateAction = "adapters-update-action",
  AdaptersHistoryView = "adapters-history-view",

  // -- bacnet-points --
  BacnetPointsView = "bacnet-points-view",
  BacnetPointsCreate = "bacnet-points-create",
  BacnetPointsUpdate = "bacnet-points-update",
  BacnetPointsDelete = "bacnet-points-delete",

  // -- devices --
  DevicesView = "devices-view",
  DevicesCreate = "devices-create",
  DevicesUpdate = "devices-update",
  DevicesDelete = "devices-delete",

  // -- offsets --
  OffsetsView = "offsets-view",
  OffsetsCreate = "offsets-create",
  OffsetsUpdate = "offsets-update",
  OffsetsDelete = "offsets-delete",
  OffsetsBulkUpdate = "offsets-bulk-update",

  // -- offset-type --
  OffsetTypeView = "offset-type-view",
  OffsetTypeCreate = "offset-type-create",
  OffsetTypeUpdate = "offset-type-update",
  OffsetTypeDelete = "offset-type-delete",

  // -- definitions --
  DefinitionsView = "definitions-view",
  DefinitionsCreate = "definitions-create",
  DefinitionsUpdate = "definitions-update",
  DefinitionsDelete = "definitions-delete",

  // -- audits --
  AuditsView = "audits-view",

  // -- special-catalogs --
  SpecialCatalogsView = "special-catalogs-view",
  SpecialCatalogsCreate = "special-catalogs-create",
  SpecialCatalogsUpdate = "special-catalogs-update",
  SpecialCatalogsDelete = "special-catalogs-delete",

  // -- special-definitions --
  SpecialDefinitionsView = "special-definitions-view",
  SpecialDefinitionsCreate = "special-definitions-create",
  SpecialDefinitionsUpdate = "special-definitions-update",
  SpecialDefinitionsDelete = "special-definitions-delete",

  // -- vendors --
  VendorsView = "vendors-view",
  VendorsCreate = "vendors-create",
  VendorsUpdate = "vendors-update",
  VendorsDelete = "vendors-delete",

  // -- datablocks --
  DatablocksView = "datablocks-view",
  DatablocksUpdate = "datablocks-update",
  DatablocksDelete = "datablocks-delete",
  DatablocksDefinitionsUpdate = "datablocks-definitions-update",
  DatablocksDefinitionsCreate = "datablocks-definitions-create",
  DatablocksCompleteView = "datablocks-complete-view",
  DatablocksCompleteCreate = "datablocks-complete-create",
  DatablocksCompleteUpdate = "datablocks-complete-update",
  DatablocksOffsetsView = "datablocks-offsets-view",
  DatablocksOffsetsDelete = "datablocks-offsets-delete",

  // -- token --
  TokenCreate = "token-create",

  // -- datablock-type --
  DatablockTypeView = "datablock-type-view",
  DatablockTypeCreate = "datablock-type-create",
  DatablockTypeUpdate = "datablock-type-update",
  DatablockTypeDelete = "datablock-type-delete",

  // -- notifications --
  NotificationsCreate = "notifications-create",
  NotificationsUpdate = "notifications-update",
  NotificationsDelete = "notifications-delete",
  NotificationsAssignView = "notifications-assign-view",
  NotificationsAssignCreate = "notifications-assign-create",
  NotificationsAssignUpdate = "notifications-assign-update",
  NotificationsAssignDelete = "notifications-assign-delete",

  // -- debounce-overrides --
  DebounceOverridesView = "debounce-overrides-view",
  DebounceOverridesCreate = "debounce-overrides-create",
  DebounceOverridesUpdate = "debounce-overrides-update",
  DebounceOverridesDelete = "debounce-overrides-delete",

  // -- externals --
  ExternalsView = "externals-view",
  ExternalsCreate = "externals-create",
  ExternalsUpdate = "externals-update",
  ExternalsDelete = "externals-delete",

  // -- external-endpoints --
  ExternalEndpointsView = "external-endpoints-view",
  ExternalEndpointsCreate = "external-endpoints-create",
  ExternalEndpointsUpdate = "external-endpoints-update",
  ExternalEndpointsDelete = "external-endpoints-delete",

  // -- external-api --
  /**
   * What an API client (an External) may do on the role-checked /api/ext
   * routes (LIFT-976, LIFT-1097, LIFT-1099). Only a role given to an External uses these,
   * and no LiftCloud rule hands them out: a role holds them only when granted.
   */
  ExtStatusView = "ext-status-view",
  ExtCommandCreate = "ext-command-create",
  ExtSubscribeView = "ext-subscribe-view",

  // -- channel_modifiers --
  ChannelModifiersView = "channel-modifiers-view",
  ChannelModifiersCreate = "channel-modifiers-create",
  ChannelModifiersEdit = "channel-modifiers-edit",
  ChannelModifiersDelete = "channel-modifiers-delete",
  ChannelModifiersBulkUpdate = "channel-modifiers-bulk-update",
  ChannelsModifiersView = "channels-modifiers-view",

  // -- channel_endpoints --
  ChannelEndpointsView = "channel-endpoints-view",
  ChannelEndpointsCreate = "channel-endpoints-create",
  ChannelEndpointsEdit = "channel-endpoints-edit",
  ChannelEndpointsDelete = "channel-endpoints-delete",
  ChannelsEndpointsView = "channels-endpoints-view",
  ChannelsEndpointsBulkUpdate = "channels-endpoints-bulk-update",

  // -- control_routes --
  ControlRoutesView = "control-routes-view",
  ControlRoutesCreate = "control-routes-create",
  /** Tree key is "-edit", not "-update" — it must match LiftCloud byte for byte. */
  ControlRoutesEdit = "control-routes-edit",
  ControlRoutesDelete = "control-routes-delete",

  // -- modbus_wizard --
  ModbusWizardParseFileCreate = "modbus-wizard-parse-file-create",
  ModbusWizardSuggestMappingsCreate = "modbus-wizard-suggest-mappings-create",
  ModbusWizardGenerateAdapterCreate = "modbus-wizard-generate-adapter-create",

  // -- general-report --
  LiftRecordsGeneralView = "lift-records-general-view",

  // -- fault-report --
  FaultRecentCreate = "fault-recent-create",
  FaultLogCreate = "fault-log-create",

  // -- fault-chart --
  FaultChartChart = "fault-chart-chart",

  // -- traffic-report --
  LiftRecordsTrafficView = "lift-records-traffic-view",

  // -- car-report --
  LiftRecordsCarView = "lift-records-car-view",

  // -- service-report --
  LiftRecordsServiceView = "lift-records-service-view",

  // -- beneficial-report --
  LiftRecordsBeneficialView = "lift-records-beneficial-view",

  // -- performance-report --
  LiftRecordsPerformanceView = "lift-records-performance-view",

  // -- out-of-service-report --
  LiftRecordsOutOfServiceView = "lift-records-out-of-service-view",

  // -- root-permissions --
  RootPermissionsView = "root-permissions-view",
  /**
   * Gates every `utils.Authorize`-checked permission endpoint whose verb is
   * "view": `GET /api/permissions/` and `GET /api/permissions/:permissionID`.
   *
   * `sanitizeURI` strips the >30-char UUID and collapses the resulting `//`, so
   * both paths reduce to the same "/api/permissions/" — one node covers both.
   * Gin's `RedirectTrailingSlash` means the slash-less form never reaches a
   * handler, which is why the former `permissions-view` node at "/permissions"
   * was dead under any prefix and has been removed rather than corrected.
   */
  PermissionView = "permission-view",
  PermissionsCreate = "permissions-create",
  PermissionsUpdate = "permissions-update",
  PermissionsDelete = "permissions-delete",

  // -- icons --
  IconsView = "icons-view",
  IconsCreate = "icons-create",
  IconsUpdate = "icons-update",
  IconsDelete = "icons-delete",

  // -- actions --
  ActionsView = "actions-view",
  ActionsCreate = "actions-create",
  ActionsUpdate = "actions-update",
  ActionsDelete = "actions-delete",
  ActionsViewByOrganization = "actions-view-by-organization",

  // -- protocol --
  ProtocolCreate = "protocol-create",
  ProtocolsViewLight = "protocols-view-light",

  // -- leading-zeroes --
  LeadingZeroesCreate = "leading-zeroes-create",

  // -- keys --
  KeysView = "keys-view",
  KeysCreate = "keys-create",
  KeysUpdate = "keys-update",
  KeysDelete = "keys-delete",
  KeysViewLight = "keys-view-light",
  /** Gates `GET /api/keys/by-server/:serverId` — the key list for one server. */
  KeysByServerView = "keys-by-server-view",

  // -- custom-keys --
  CustomKeysView = "custom-keys-view",
  CustomKeysCreate = "custom-keys-create",
  CustomKeysUpdate = "custom-keys-update",
  CustomKeysDelete = "custom-keys-delete",

  // -- maps --
  MapsView = "maps-view",
  MapsCreate = "maps-create",
}

/**
 * Typed representation of the permission tree received from the API.
 */
export interface PermissionNode {
  tree_key: string;
  active: boolean;
  offline: boolean;
  sub_pages?: PermissionNode[];
  actions?: PermissionNode[];
}

export interface PermissionTree {
  pages?: PermissionNode[];
}

/**
 * Walk the permission tree once and collect every tree_key whose
 * active (EDGE mode) or offline (non-EDGE mode) flag is true.
 *
 * Returns a flat Set for O(1) lookups — replaces the old nested-loop search.
 */
export function flattenPermissions(permissions: PermissionTree | null | undefined): Set<string> {
  const result = new Set<string>();
  if (!permissions?.pages) return result;

  const isEdge = process.env.REACT_APP_EDGE_SERVER_CONNECTION === "EDGE";

  function visit(node: PermissionNode): void {
    // Pages have both active + offline; actions only have active (no offline field).
    // When offline is undefined (actions/FlatBuffer), fall back to active.
    const enabled = isEdge ? node.active : (node.offline ?? (node as any).offline ?? node.active);
    // Support both snake_case (JSON) and camelCase (FlatBuffer) field names
    const treeKey = node.tree_key ?? (node as any).treeKey;
    if (enabled && treeKey) {
      result.add(treeKey);
    }
    const subPages = node.sub_pages ?? (node as any).subPages;
    if (subPages) {
      subPages.forEach(visit);
    }
    if (node.actions) {
      node.actions.forEach(visit);
    }
  }

  permissions.pages.forEach(visit);
  return result;
}

/**
 * Permission check against the user's flattened permission list.
 *
 * The list is a plain `string[]` in Redux state (not a Set) so the state stays
 * serializable; role permission lists are short, so the linear scan is cheap.
 *
 * The special sentinel "permission-not-defined" always returns true,
 * matching the legacy behavior.
 */
export function hasPermission(permission: Permission | string, permissionSet: string[]): boolean {
  if (permission === Permission.PermissionNotDefined) return true;
  return permissionSet.includes(permission);
}
